import AddIcon from "@mui/icons-material/Add"; // Plus icon for adding columns
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { MoreHorizIcon } from "@platformx/shared/static-assets";
import { formatTitle, frameTitle } from "@platformx/utilities";
import { useEffect, useRef, useState } from "react";
import { ADMIN, createAndUpdateRole, isDisabled } from "../../helper/helper";
import DeleteRole from "../DeleteRole/DeleteRole";
import { useCustomStyle } from "./PermissionAssignatation.style";
import SkeletonTable from "./SkeltonTable";
import TopHeader from "./TopHeader";

const PermissionTable = ({ siteModuleList, loading, setTriggerUpdate, setLoading }) => {
  const classes = useCustomStyle();
  // State for toggling the module collapse/expand using an array
  const [expandedModules, setExpandedModules] = useState(
    new Array(siteModuleList?.rolePermissionList?.length)?.fill(false),
  );
  const [columns, setColumns] = useState<any>([]);
  const [checkedPermissions, setCheckedPermissions] = useState({}); // Updated: Map to track checked items
  const [newRole, setNewRole] = useState(""); //for create new role
  const [addNewRoleButtonEnable, setaddNewRoleButtonEnable] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false); // State for DeleteRole modal
  const prevpermissionRef = useRef({});
  const inputRef = useRef<HTMLInputElement>(null);
  //Create a map from rolePermissionList for quick lookup
  useEffect(() => {
    if (siteModuleList?.rolePermissionList?.length !== 0) {
      const permissionMap = {};
      siteModuleList?.rolePermissionList?.forEach((role) => {
        role?.permissions?.forEach((module) => {
          module?.actions?.forEach((action) => {
            permissionMap[`${role.id}-${module.category}-${module.sub_category}-${action.id}`] =
              true;
          });
        });
      });
      setCheckedPermissions(permissionMap);

      const { updateRole = [] } = createAndUpdateRole(permissionMap) || {};
      prevpermissionRef.current = updateRole;
      setColumns([...siteModuleList.rolePermissionList]);
    }
  }, [siteModuleList]);

  // Toggle function for specific module by index
  const toggleModule = (index) => {
    const updatedModules = [...expandedModules];
    updatedModules[index] = !updatedModules[index];
    setExpandedModules(updatedModules);
  };

  // Toggle Parent Checkbox (Check/Uncheck all submodules for a role)
  const toggleParentCheckbox = (moduleIndex, role) => {
    setCheckedPermissions((prev) => {
      const newPermissions = { ...prev };
      const module = siteModuleList.modulePermissionList[moduleIndex];
      const isChecked = !prev[`${role}-${module.category}-${module.sub_category}`];
      newPermissions[`${role}-${module.category}-${module.sub_category}`] = isChecked;
      module.actions.forEach((action) => {
        newPermissions[`${role}-${module.category}-${module.sub_category}-${action.id}`] =
          isChecked;
      });
      return newPermissions;
    });
  };

  // Toggle Submodule Checkbox (Check/uncheck individual submodules)
  const toggleSubmoduleCheckbox = (moduleIndex, submodule, role) => {
    setCheckedPermissions((prev) => {
      const newPermissions = { ...prev };
      const module = siteModuleList.modulePermissionList[moduleIndex];
      newPermissions[`${role}-${module.category}-${module.sub_category}-${submodule.id}`] =
        !prev[`${role}-${module.category}-${module.sub_category}-${submodule.id}`];

      // If all actions are selected, check the parent module checkbox
      const allChecked = module.actions.every(
        (action) =>
          newPermissions[`${role}-${module.category}-${module.sub_category}-${action.id}`],
      );
      newPermissions[`${role}-${module.category}-${module.sub_category}`] = allChecked;
      return newPermissions;
    });
  };
  const addNewColumn = () => {
    setColumns((prevColumns) => [
      ...prevColumns,
      { id: "newColumn", role: `NewRole${prevColumns.length + 1}` },
    ]);
    setaddNewRoleButtonEnable(false); //Disable till not saved newly created role
    setNewRole(`NewRole${columns?.length + 1}`);
    setTimeout(() => {
      if (inputRef?.current) {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }, 100);
  };

  const handleMenuClick = (event, col) => {
    // Open menu on clicking MoreHorizIcon
    setAnchorEl(event.currentTarget);
    setSelectedColumn(col);
  };

  // Open DeleteRole modal and close menu
  const handleDelete = () => {
    setAnchorEl(null); // Close Menu
    setOpenDeleteModal(true); // Open Delete Modal
  };
  const formTitleAndUpdateState = (rolename) => {
    const updatedTitle = frameTitle(rolename);
    setNewRole(updatedTitle);
  };
  const handleRoleName = (tragetValue) => {
    const value = formatTitle(tragetValue);
    setNewRole(value);
  };

  // Close DeleteRole modal
  return (
    <Box className={`${classes.PermissionAssignatationWrapper} PermissionAssignatation`}>
      <TopHeader
        checkedPermissions={checkedPermissions}
        prevpermissionRef={prevpermissionRef}
        setLoading={setLoading}
        newRole={newRole}
        setNewRole={setNewRole}
        setaddNewRoleButtonEnable={setaddNewRoleButtonEnable}
        addNewRoleButtonEnable={addNewRoleButtonEnable}
        setTriggerUpdate={setTriggerUpdate}
      />
      <TableContainer
        className='permissionTableContainer'
        sx={{ maxWidth: "100%", overflowX: "auto" }}>
        {!loading && columns?.length > 0 ? (
          <Table
            size='small'
            className='permissionTable'
            sx={{ minWidth: "100%", width: columns?.length * 150 + 200 }}>
            <TableHead>
              <TableRow className='tableHeader'>
                <TableCell sx={{ width: 180, background: "#f2fafe !important" }}>
                  Permissions
                </TableCell>
                {/* Dynamically generate columns */}
                {columns.map((column, index) => (
                  <TableCell key={column.id} sx={{ width: 100 }}>
                    <Typography variant='labelregular' sx={{ display: "block" }}>
                      Role
                    </Typography>

                    {/* Plus icon to add a new column */}
                    {columns?.length === index + 1 ? (
                      <>
                        {addNewRoleButtonEnable ? (
                          <>
                            <IconButton onClick={addNewColumn} className='addButton'>
                              <AddIcon />
                            </IconButton>
                            <Typography variant='h6regular'>{column?.role}</Typography>
                            {!isDisabled(column?.role, null) && (
                              <IconButton
                                className='moreIcon'
                                onClick={(e) => handleMenuClick(e, column)}>
                                <img
                                  src={MoreHorizIcon}
                                  style={{ objectFit: "cover" }}
                                  alt='horizontal ellipsis icon'
                                />
                              </IconButton>
                            )}
                          </>
                        ) : (
                          <TextField
                            id='standard-basic'
                            value={newRole}
                            variant='standard'
                            inputRef={inputRef}
                            onChange={(e) => {
                              handleRoleName(e?.target?.value);
                            }}
                            onBlur={(e) => formTitleAndUpdateState(e?.target?.value)}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <Typography variant='h6regular'>{column?.role}</Typography>
                        {!isDisabled(column?.role, null) && (
                          <IconButton
                            className='moreIcon'
                            onClick={(e) => handleMenuClick(e, column)}>
                            <img
                              src={MoreHorizIcon}
                              style={{ objectFit: "cover" }}
                              alt='horizontal ellipsis icon'
                            />
                          </IconButton>
                        )}
                      </>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {siteModuleList?.modulePermissionList?.map((module, moduleIndex) => (
                <>
                  {/* Render TableRow only if the current category is not equal to the next category */}
                  {(moduleIndex === 0 ||
                    siteModuleList?.modulePermissionList?.[moduleIndex]?.category !==
                      siteModuleList?.modulePermissionList?.[moduleIndex - 1]?.category) && (
                    <TableRow key={moduleIndex} className='moduleHeading'>
                      <TableCell sx={{ borderRight: "none !important" }}>
                        <Typography
                          variant='h5semibold'
                          pt={1.5}
                          pb={1.5}
                          sx={{ textTransform: "uppercase" }}>
                          {siteModuleList?.modulePermissionList?.[moduleIndex]?.display_category ||
                            siteModuleList?.modulePermissionList?.[moduleIndex]?.category}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow key={moduleIndex} className={"categoryNameRow"}>
                    <TableCell>
                      <Box
                        onClick={() => toggleModule(moduleIndex)}
                        style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                        {expandedModules[moduleIndex] ? (
                          <KeyboardArrowUpIcon style={{ marginRight: "4px" }} />
                        ) : (
                          <KeyboardArrowDownIcon style={{ marginRight: "4px" }} />
                        )}
                        <Typography variant='p3semibold'>
                          {module?.display_sub_category || module?.sub_category || module?.category}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Render checkbox for each dynamic column */}
                    {columns.map((column) => {
                      const selectedModule = siteModuleList.modulePermissionList[moduleIndex];
                      const allChecked = selectedModule.actions.every(
                        (action) =>
                          checkedPermissions[
                            `${column.id}-${selectedModule.category}-${selectedModule.sub_category}-${action.id}`
                          ],
                      );
                      const someChecked = selectedModule.actions.some(
                        (action) =>
                          checkedPermissions[
                            `${column.id}-${selectedModule.category}-${selectedModule.sub_category}-${action.id}`
                          ],
                      );

                      return (
                        <TableCell
                          key={column.id}
                          className={isDisabled(column.role, module) ? "disabledColumn" : ""}>
                          <Checkbox
                            disabled={isDisabled(column.role, module)}
                            checked={allChecked} // Parent checkbox is fully checked if all child checkboxes are checked
                            indeterminate={!allChecked && someChecked} // Parent checkbox is indeterminate if some (but not all) child checkboxes are checked
                            onChange={() => toggleParentCheckbox(moduleIndex, column.id)} // Toggles all child checkboxes
                            name={column.id}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {expandedModules[moduleIndex] &&
                    module?.actions?.map((submodule) => (
                      <TableRow key={submodule?.id} className='categoryPermissionRow'>
                        <TableCell>{submodule.name}</TableCell>
                        {columns?.map((column) => (
                          <TableCell
                            key={column.id}
                            className={isDisabled(column.role, module) ? "disabledColumn" : ""}>
                            <Checkbox
                              name={submodule.id}
                              disabled={isDisabled(column.role, module)}
                              checked={
                                !!checkedPermissions[
                                  `${column.id}-${module.category}-${module.sub_category}-${submodule.id}`
                                ]
                              }
                              onChange={() =>
                                toggleSubmoduleCheckbox(moduleIndex, submodule, column.id)
                              }
                              sx={{ color: "#8d8989" }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </>
              ))}
            </TableBody>
          </Table>
        ) : loading || columns?.length === 0 ? (
          <SkeletonTable />
        ) : null}
      </TableContainer>

      {/* Menu (opens when MoreHorizIcon is clicked) */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      {openDeleteModal && (
        <DeleteRole
          openModal={openDeleteModal}
          setOpenModal={setOpenDeleteModal}
          selectedColumn={selectedColumn}
          setSelectedColumn={setSelectedColumn}
          setTriggerUpdate={setTriggerUpdate}
          columns={columns?.filter(
            (column) => column?.id !== selectedColumn?.id && column?.role !== ADMIN,
          )}
        />
      )}
    </Box>
  );
};

export default PermissionTable;
